import type { SelectableColumn } from "@/hooks/use-column-selection";
import { usePersonColumnSelection } from "@/hooks/use-column-selection";
import { getPersonColumns } from "@/pages/persons/person-list/person-columns";
import { EntityColumnSelector } from "./entity-column-selector";

interface PersonsColumnSelectorProps {
  // Optional: provide column state externally (for future integrations)
  columnState?: {
    availableColumns: SelectableColumn[];
    isColumnSelected: (columnId: string) => boolean;
    toggleColumn: (columnId: string) => void;
    isLoading?: boolean;
    error?: Error | null;
  };
}

/**
 * Component for managing person column selection including both core and custom fields.
 * Displays a popover with checkboxes for selecting which columns to display.
 * Can accept external column state, or use its own hooks.
 */
export function PersonsColumnSelector({
  columnState,
}: PersonsColumnSelectorProps = {}) {
  // Get the core columns
  const coreColumns = getPersonColumns();

  // Use provided state OR internal hook
  const internalState = usePersonColumnSelection(
    columnState ? [] : coreColumns,
  );
  const { availableColumns, isColumnSelected, toggleColumn, isLoading, error } =
    columnState || internalState;

  return (
    <EntityColumnSelector
      availableColumns={availableColumns}
      isColumnSelected={isColumnSelected}
      toggleColumn={toggleColumn}
      isLoading={isLoading || false}
      error={error || null}
      buttonLabel="Columns"
      noColumnsMessage="No columns available."
      errorMessage="Error loading columns."
    />
  );
}
